import { Children, createElement, isValidElement, useEffect, useRef, useState } from 'react';

/**
 * The landing page's motion kit — small, dependency-free primitives the
 * sections compose: reveal-on-scroll, word and letter reveals, a decode for
 * labels, an odometer, voice bars, a self-pausing cycle for tabs and quotes,
 * a magnetic hover and a pointer spotlight.
 *
 * Every primitive answers to `prefers-reduced-motion`: reveals render shown,
 * counters show their value, timers that rotate content do not start. The CSS that draws them lives in theme/landing.css under `.lx-*`
 * names — design.css is global and owns `.num`, `.card`, `.bar` and friends,
 * so nothing here borrows a bare name.
 */

const REDUCED = '(prefers-reduced-motion: reduce)';

/** One-shot read, for code that runs outside render (rAF loops, observers). */
export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(REDUCED).matches;

/** True while a media query matches — the pinned scenes unpin below 900px. */
export function useMedia(query) {
  const [match, setMatch] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(query).matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    on();
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [query]);
  return match;
}

/* One IntersectionObserver per option set, shared by every element that asks
   for it — a page of reveals is dozens of elements, not dozens of observers. */
const observers = new Map();
function observe(el, opts, cb) {
  const key = `${opts.threshold}|${opts.rootMargin}`;
  let entry = observers.get(key);
  if (!entry) {
    const callbacks = new Map();
    const io = new IntersectionObserver((es) => es.forEach((e) => callbacks.get(e.target)?.forEach((f) => f(e))), opts);
    entry = { io, callbacks };
    observers.set(key, entry);
  }
  if (!entry.callbacks.has(el)) entry.callbacks.set(el, new Set());
  entry.callbacks.get(el).add(cb);
  entry.io.observe(el);
  return () => {
    const set = entry.callbacks.get(el);
    set?.delete(cb);
    if (set && !set.size) { entry.callbacks.delete(el); entry.io.unobserve(el); }
  };
}

/**
 * Whether the element has entered the viewport. `once` (the default) latches
 * on first sight — reveals never replay on the way back up, which is what
 * keeps a long page calm. With `once: false` it tracks visibility, which is
 * how loops pause while offscreen.
 */
export function useInView(ref, { once = true, threshold = 0.18, rootMargin = '0px 0px -8% 0px' } = {}) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined' || (once && prefersReducedMotion())) {
      setInView(true);
      return undefined;
    }
    let stop = null;
    stop = observe(el, { threshold, rootMargin }, (e) => {
      if (e.isIntersecting) {
        setInView(true);
        if (once) stop?.();
      } else if (!once) setInView(false);
    });
    return () => stop?.();
  }, [ref, once, threshold, rootMargin]);
  return inView;
}

/**
 * Fade-and-rise on first sight. `delay` is seconds; `variant` picks the
 * entrance (up · fade · scale · left), all of it drawn in CSS from the
 * `data-shown` flag so a reveal costs one attribute write.
 */
export function Reveal({ as = 'div', delay = 0, variant = 'up', className = '', style, children, ...rest }) {
  const ref = useRef(null);
  const shown = useInView(ref);
  return createElement(as, {
    ref,
    className: `lx-reveal ${className}`.trim(),
    'data-variant': variant,
    'data-shown': shown ? '' : undefined,
    style: { ...style, '--d': `${delay}s` },
    ...rest,
  }, children);
}

/**
 * Headline words rising out of a mask, staggered. Strings split on spaces;
 * elements pass through as one unit, so a coloured phrase stays whole:
 *
 *   <Words>Every basis point, <em>accounted for.</em></Words>
 *
 * `start` offsets the stagger (seconds) so stacked headlines read in order.
 * `play` lets the caller own the trigger (the hero plays on mount).
 */
export function Words({ as = 'span', children, start = 0, step = 0.045, play, className = '' }) {
  const ref = useRef(null);
  const seen = useInView(ref);
  const shown = play ?? seen;
  const units = [];
  Children.toArray(children).forEach((child) => {
    if (typeof child === 'string') child.split(/(\s+)/).forEach((t) => { if (t) units.push(t); });
    else units.push(child);
  });
  let i = 0;
  return createElement(as, {
    ref, className: `lx-words ${className}`.trim(), 'data-shown': shown ? '' : undefined,
  }, units.map((u, k) => {
    if (typeof u === 'string' && /^\s+$/.test(u)) return ' ';
    const d = start + (i++) * step;
    return (
      // Position is the identity: the text never reorders.
      // eslint-disable-next-line react/no-array-index-key
      <span className="lx-w" key={k}><span style={{ '--d': `${d}s` }}>{u}</span></span>
    );
  }));
}

/**
 * A phrase that swaps letter by letter — the outgoing one blurs up and away,
 * the incoming one settles in from below. Used for the hero's rotating claim:
 * it reads as one continuous line, with none of the width jitter a character
 * scramble gives a proportional serif.
 */
export function LetterSwap({ text, className = '' }) {
  const [shown, setShown] = useState(text);
  const [leaving, setLeaving] = useState(null);
  useEffect(() => {
    if (text === shown) return undefined;
    if (prefersReducedMotion()) { setShown(text); return undefined; }
    setLeaving(shown);
    setShown(text);
    const t = setTimeout(() => setLeaving(null), 700);
    return () => clearTimeout(t);
    // Driven by `text` alone; `shown` is the value being replaced.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  const letters = (s) => [...s].map((ch, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <span key={i} style={{ '--i': i }}>{ch === ' ' ? ' ' : ch}</span>
  ));
  return (
    <span className={`lx-swap ${className}`.trim()}>
      <span className="lx-swap-in" key={shown} aria-hidden="true">{letters(shown)}</span>
      {leaving && <span className="lx-swap-out" key={`out-${leaving}`} aria-hidden="true">{letters(leaving)}</span>}
      <span className="lx-sr">{shown}</span>
    </span>
  );
}

/**
 * A short decode for technical labels ("VERDICT · 1.4s"): characters settle
 * left to right out of digits and symbols. Monospace-ish UI text only — see
 * LetterSwap for headlines.
 */
const GLYPHS = '0123456789%₹+−.αβ#';
export function Scramble({ text, play = true, duration = 700, className = '' }) {
  const [out, setOut] = useState(play ? '' : text);
  useEffect(() => {
    if (!play) return undefined;
    if (prefersReducedMotion()) { setOut(text); return undefined; }
    let raf; let last = 0;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      if (now - last > 40 || p === 1) {
        last = now;
        const settled = Math.floor(p * text.length);
        setOut([...text].map((ch, i) => {
          if (i < settled || ch === ' ' || ch === '·') return ch;
          return GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
        }).join(''));
      }
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text, play, duration]);
  return <span className={className}><span aria-hidden="true">{out || '\u00a0'}</span><span className="lx-sr">{text}</span></span>;
}

/**
 * Voice bars: they breathe while `live`, and sit low and still otherwise.
 * Heights are fixed per bar so the shape reads as speech, not noise.
 */
export function Wave({ bars = 18, live = true, className = '' }) {
  return (
    <span className={`lx-wave ${live ? 'live' : ''} ${className}`.trim()} aria-hidden="true">
      {Array.from({ length: bars }, (_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <i key={i} style={{ '--i': i, '--h': `${30 + ((i * 37) % 70)}%` }} />
      ))}
    </span>
  );
}

/**
 * A rolling-digit number. Every digit is a 0–9 strip that slides to its
 * value; everything else (₹, commas, %, units) sits still. Rolls when it
 * first scrolls into view, or when `play` flips true.
 */
export function Odometer({ value, play, className = '', stagger = 0.04 }) {
  const ref = useRef(null);
  const seen = useInView(ref, { threshold: 0.4 });
  const go = play ?? seen;
  const chars = [...String(value)];
  const digitCount = chars.filter((c) => /\d/.test(c)).length;
  let di = 0;
  return (
    <span ref={ref} className={`lx-odo ${className}`.trim()} data-go={go ? '' : undefined}>
      <span className="lx-sr">{String(value)}</span>
      {chars.map((c, i) => {
        if (!/\d/.test(c)) return <span key={i} className="lx-odo-s" aria-hidden="true">{c}</span>; // eslint-disable-line react/no-array-index-key
        const n = +c;
        const d = (digitCount - 1 - di++) * stagger;
        return (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i} className="lx-odo-d" aria-hidden="true">
            <span style={{ '--n': n, '--d': `${d}s` }}>{'0123456789'.split('').map((x) => <i key={x}>{x}</i>)}</span>
          </span>
        );
      })}
    </span>
  );
}

/**
 * A number eased from 0 to `to` once it is on screen — for figures inside
 * mocks, where a rolling strip would be too much.
 */
export function useCountUp(to, { play = true, duration = 1100, delay = 0 } = {}) {
  const [v, setV] = useState(play && !prefersReducedMotion() ? 0 : to);
  useEffect(() => {
    if (!play) return undefined;
    if (prefersReducedMotion()) { setV(to); return undefined; }
    let raf; let t0 = null;
    const step = (now) => {
      if (t0 == null) t0 = now + delay;
      const p = Math.max(0, Math.min(1, (now - t0) / duration));
      setV(to * (1 - (1 - p) ** 3));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, play, duration, delay]);
  return v;
}

const FINE = '(hover: hover) and (pointer: fine)';

/**
 * Pointer position written onto an element as `--mx`/`--my` (px) and
 * `--px`/`--py` (−1…1 from centre). CSS does the drawing — spotlights,
 * tilts, parallax — so a pointer move never re-renders React.
 */
export function usePointerVars(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !window.matchMedia?.(FINE).matches || prefersReducedMotion()) return undefined;
    let raf = 0; let ev = null;
    const paint = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      const x = ev.clientX - r.left; const y = ev.clientY - r.top;
      el.style.setProperty('--mx', `${x}px`);
      el.style.setProperty('--my', `${y}px`);
      el.style.setProperty('--px', ((x / r.width) * 2 - 1).toFixed(3));
      el.style.setProperty('--py', ((y / r.height) * 2 - 1).toFixed(3));
    };
    const move = (e) => { ev = e; if (!raf) raf = requestAnimationFrame(paint); };
    const leave = () => {
      el.style.setProperty('--px', '0');
      el.style.setProperty('--py', '0');
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerleave', leave);
    return () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerleave', leave);
      cancelAnimationFrame(raf);
    };
  }, [ref]);
}

/** A card whose border and surface light up under the pointer. */
export function Spotlight({ as = 'div', className = '', children, ...rest }) {
  const ref = useRef(null);
  usePointerVars(ref);
  return createElement(as, { ref, className: `lx-spot ${className}`.trim(), ...rest }, children);
}

/**
 * The primary CTA's pull toward the pointer — a few pixels, springing back on
 * leave. Fine pointers only; on touch it is a plain button.
 */
export function Magnetic({ children, strength = 0.22 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !window.matchMedia?.(FINE).matches || prefersReducedMotion()) return undefined;
    const move = (e) => {
      const r = el.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      el.style.transform = `translate(${dx * strength}px, ${dy * strength}px)`;
    };
    const leave = () => { el.style.transform = ''; };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerleave', leave);
    return () => { el.removeEventListener('pointermove', move); el.removeEventListener('pointerleave', leave); };
  }, [strength]);
  const child = Children.only(children);
  return <span className="lx-magnet" ref={ref}>{isValidElement(child) ? child : null}</span>;
}

/**
 * A timer that cycles `0…count-1` every `ms` while the element is on screen
 * and nobody is interacting with it. Returns [index, setIndex, pause] — a
 * manual pick pauses the cycle until `resume()`; reduced motion never starts it.
 */
export function useCycle(count, ms, ref) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const visible = useInView(ref, { once: false, threshold: 0.3, rootMargin: '0px' });
  useEffect(() => {
    if (paused || !visible || count < 2 || prefersReducedMotion()) return undefined;
    const t = setTimeout(() => setI((x) => (x + 1) % count), ms);
    return () => clearTimeout(t);
  }, [i, paused, visible, count, ms]);
  const pick = (n) => { setPaused(true); setI(n); };
  return { index: i, pick, paused, running: !paused && visible, pause: () => setPaused(true), resume: () => setPaused(false) };
}
